import { websiteConfig } from '@/config/website';
import { getLocale, localeConfig } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { getCanonicalUrl, getImageUrl } from '@/lib/urls';
import { getDwpCopy } from './dwp-copy';

export const DWP_PATH = Routes.DevilWearsPradaAi;
export const DWP_PUBLISHED = '2026-10-18';
export const DWP_LAST_UPDATED = '2026-10-18';
export const DWP_OG_IMAGE = '/devil-wears-prada-ai/devil-wears-prada-ai-og.jpg';

/**
 * JSON-LD for /devil-wears-prada-ai. Every text field is read from
 * `dwp-copy.ts`, the same constants the visible page renders.
 */
export function createDwpJsonLd() {
  const copy = getDwpCopy();
  const siteName = websiteConfig.metadata?.name ?? '';
  const homeUrl = getCanonicalUrl(Routes.Root);
  const url = getCanonicalUrl(DWP_PATH);
  const organizationId = `${homeUrl}#organization`;
  const inLanguage = localeConfig[getLocale()].hreflang;
  const logoPath = websiteConfig.metadata?.images?.logoLight;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: siteName,
        url: homeUrl,
        ...(logoPath
          ? { logo: { '@type': 'ImageObject', url: getImageUrl(logoPath) } }
          : {}),
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: copy.seoTitle,
        description: copy.seoDescription,
        inLanguage,
        datePublished: `${DWP_PUBLISHED}T00:00:00+08:00`,
        dateModified: `${DWP_LAST_UPDATED}T00:00:00+08:00`,
        isPartOf: { '@type': 'WebSite', name: siteName, url: homeUrl },
        publisher: { '@id': organizationId },
        about: { '@type': 'Thing', name: 'Devil Wears Prada AI video trend' },
        mainEntity: { '@id': `${url}#software` },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: getImageUrl(DWP_OG_IMAGE),
          width: 1200,
          height: 630,
        },
        breadcrumb: { '@id': `${url}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: copy.breadcrumbHome,
            item: homeUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: copy.breadcrumbCurrent,
            item: url,
          },
        ],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${url}#software`,
        name: copy.title,
        applicationCategory: 'MultimediaApplication',
        applicationSubCategory: 'AI video generator',
        operatingSystem: 'Web',
        description: copy.quickAnswer,
        url,
        inLanguage,
        publisher: { '@id': organizationId },
        featureList: copy.features.items.map((feature) => feature.title),
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          url: getCanonicalUrl(Routes.Pricing),
        },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: copy.steps.title,
        step: copy.steps.items.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: copy.faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };
}

export function DwpJsonLd() {
  return (
    <script
      type="application/ld+json"
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: JSON.stringify(createDwpJsonLd()) }}
    />
  );
}
